/**
 * Мастер опроса по экологии (ecology-survey, макет варианта Б 17.09 + макет клиента 16.09): опрос
 * проходится по каждой площадке отдельно. Шаги: «Площадка» (выбор площадки, подстановка ответов с
 * другой площадки), четыре раздела вопросов, «Проверка» (ответы с «Изменить»; незаполненное не
 * мешает завершить — менеджер уточнит), затем экран результата.
 *
 * Черновик ответов сохраняется на сервере при переходе между шагами. «Завершить опрос» отправляет
 * ответы — сервер применяет правила к строкам площадки и отвечает результатом и свежим обзором.
 * Один компонент для клиента и для команды (отличаются API скоупа и ссылки выхода).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, MapPin } from 'lucide-react'
import type {
  EcoAnswers,
  EcoCatalogItem,
  EcoOverview,
  EcoQuestion,
  EcoScopeApi,
  EcoSite,
  EcoSurvey,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_SURVEY_SECTIONS,
  ecoAnswerText,
  ecoSiteCategoryLabel,
  ecoSurveyStatusText,
  isEcoAnswered,
  isEcoQuestionVisible,
  visibleEcoQuestions,
} from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import { Stepper } from '@/design-system/components/data/stepper'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { QuestionField } from './question-field'
import { SurveyResult } from './survey-result'

const STEPS = ['Площадка', ...ECO_SURVEY_SECTIONS, 'Проверка']
const REVIEW_STEP = STEPS.length - 1
/** «Не подставлять»: Radix Select не допускает пустое значение пункта. */
const NO_COPY = 'none'

/** Ссылки выхода из мастера (у клиента — подразделы «Экологии», у команды — карточка клиента). */
export interface SurveyExitLinks {
  back: { label: string; onClick: () => void }
  after: { label: string; onClick: () => void }[]
}

function SiteCard({
  site,
  selected,
  onSelect,
}: {
  site: EcoSite
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-10 border px-4 py-3 text-left transition-colors',
        selected ? 'border-accent bg-accent-bg' : 'border-line bg-white hover:bg-hover',
      )}
    >
      <MapPin className="mt-0.5 size-4 shrink-0 stroke-current text-txt-tertiary" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-txt">{site.name}</span>
        <span className="block text-13 text-txt-secondary">
          {[site.address, `опрос ${ecoSurveyStatusText(site)}`].filter(Boolean).join(' · ')}
        </span>
      </span>
      <Badge variant={site.category === '' ? 'amber' : 'gray'} className="shrink-0">
        {ecoSiteCategoryLabel(site.category)}
      </Badge>
    </button>
  )
}

export function SurveyWizard({
  api,
  overview,
  questions,
  catalog,
  initialSiteId,
  exit,
  onOverviewChange,
  onOrder,
}: {
  api: EcoScopeApi
  overview: EcoOverview
  /** Включённые вопросы в порядке опроса. */
  questions: EcoQuestion[]
  /** Справочник услуг: по нему видно, что из результата опроса можно заказать. */
  catalog: EcoCatalogItem[]
  /** Площадка, с которой открыт опрос (`?site=`). */
  initialSiteId: string | null
  exit: SurveyExitLinks
  onOverviewChange: (overview: EcoOverview) => void
  /** Заказать недостающее по этой площадке (ecology-order); у команды не показывается. */
  onOrder?: (input: { catalogItemId: string; siteIds: string[] }) => void
}) {
  const [step, setStep] = useState(0)
  const [siteId, setSiteId] = useState<string | null>(
    overview.sites.some((s) => s.id === initialSiteId)
      ? initialSiteId
      : overview.sites.length === 1
        ? overview.sites[0].id
        : null,
  )
  const [answers, setAnswers] = useState<EcoAnswers>({})
  const [survey, setSurvey] = useState<EcoSurvey | null>(null)
  const [loadingSurvey, setLoadingSurvey] = useState(false)
  const [copyFrom, setCopyFrom] = useState(NO_COPY)
  const [confirmCopy, setConfirmCopy] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<EcoSurvey | null>(null)
  const topRef = useRef<HTMLDivElement>(null)

  const site = overview.sites.find((s) => s.id === siteId) ?? null
  const donors = overview.sites.filter((s) => s.id !== siteId && s.survey.status !== 'none')

  // Ответы выбранной площадки: сохранённый черновик или пройденный опрос.
  useEffect(() => {
    if (!siteId) return
    const controller = new AbortController()
    setLoadingSurvey(true)
    setError(null)
    setCopyFrom(NO_COPY)
    api
      .getSurvey(siteId, controller.signal)
      .then((sv) => {
        if (controller.signal.aborted) return
        setSurvey(sv)
        setAnswers(sv.answers)
        setDirty(false)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить опрос площадки'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingSurvey(false)
      })
    return () => controller.abort()
  }, [api, siteId])

  useEffect(() => {
    topRef.current?.scrollIntoView?.({ block: 'start' })
  }, [step, done])

  const hasAnswers = Object.values(answers).some(isEcoAnswered)
  const visible = useMemo(
    () => questions.filter((q) => isEcoQuestionVisible(questions, answers, q.id)),
    [questions, answers],
  )
  const unanswered = visible.filter((q) => !isEcoAnswered(answers[q.id])).length

  function change(id: string, next: EcoAnswers[string]) {
    setAnswers((prev) => ({ ...prev, [id]: next }))
    setDirty(true)
  }

  async function copyAnswers(donorId: string) {
    setError(null)
    try {
      const donor = await api.getSurvey(donorId)
      setAnswers(donor.answers)
      setCopyFrom(donorId)
      setDirty(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось подставить ответы'))
    }
  }

  /** Переход между шагами: несохранённые ответы уходят в черновик на сервере. */
  async function go(next: number) {
    if (dirty && siteId) {
      setSaving(true)
      setError(null)
      try {
        const saved = await api.saveSurvey(siteId, answers)
        setSurvey(saved)
        setDirty(false)
      } catch (err) {
        setError(getErrorMessage(err, 'Не удалось сохранить ответы'))
        setSaving(false)
        return
      }
      setSaving(false)
    }
    setStep(next)
  }

  async function finish() {
    if (!siteId || finishing) return
    setFinishing(true)
    setError(null)
    try {
      const res = await api.completeSurvey(siteId, answers)
      setSurvey(res.survey)
      setDirty(false)
      onOverviewChange(res.overview)
      setDone(res.survey)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось завершить опрос'))
    } finally {
      setFinishing(false)
    }
  }

  if (done?.result && site) {
    return (
      <div ref={topRef} className="scroll-mt-4">
        <Card className="mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-round bg-green-bg text-green">
              <Check className="size-5 stroke-current" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-16 font-semibold text-txt">
                Опрос по площадке «{site.name}» завершён
              </h2>
              <p className="text-13 text-txt-secondary">
                Строки добавлены в «Проектные работы», «Отчётность» и «Лабораторный контроль». Лишнее
                можно убрать там же.
              </p>
            </div>
          </div>
        </Card>
        <SurveyResult
          result={done.result}
          siteHasCategory={site.category !== ''}
          catalog={catalog}
          onOrder={
            onOrder ? (catalogItemId) => onOrder({ catalogItemId, siteIds: [site.id] }) : undefined
          }
        />
        <div className="mt-4 flex flex-wrap gap-2">
          {exit.after.map((link, i) => (
            <Button
              key={link.label}
              variant={i === 0 ? 'primary' : 'secondary'}
              onClick={link.onClick}
            >
              {link.label}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  const nextLabel = step < REVIEW_STEP ? `Далее — ${STEPS[step + 1].toLowerCase()}` : ''

  return (
    <div ref={topRef} className="scroll-mt-4">
      <Stepper steps={STEPS} current={step} className="mb-3" />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-12 text-txt-tertiary">
        <span>
          Шаг {step + 1} из {STEPS.length} — {STEPS[step].toLowerCase()}
          {site && step > 0 ? ` · ${site.name}` : ''}
        </span>
        <span aria-live="polite">
          {saving ? 'Сохранение…' : survey?.updatedAt && !dirty ? 'Ответы сохранены' : ''}
        </span>
      </div>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {step === 0 ? (
        <div className="flex flex-col gap-4">
          <Card header="По какой площадке опрос?">
            {overview.sites.length === 0 ? (
              <p className="text-13 text-txt-secondary">
                Площадок нет — добавьте площадку в разделе «Экология».
              </p>
            ) : (
              <div className="flex flex-col gap-2" role="group" aria-label="Площадка">
                {overview.sites.map((s) => (
                  <SiteCard
                    key={s.id}
                    site={s}
                    selected={s.id === siteId}
                    onSelect={() => setSiteId(s.id)}
                  />
                ))}
              </div>
            )}
            {site?.survey.status === 'completed' ? (
              <p className="mt-3 text-13 text-txt-secondary">
                Опрос по этой площадке уже пройден — ответы подставлены. Их можно поправить и
                завершить опрос заново: кабинет добавит недостающее, внесённые данные не пропадут.
              </p>
            ) : null}
          </Card>

          {site && donors.length > 0 ? (
            <Card header="Подставить ответы с другой площадки">
              <div className="max-w-md">
                <Select
                  value={copyFrom}
                  disabled={loadingSurvey}
                  onValueChange={(v) => {
                    if (v === NO_COPY) return
                    if (hasAnswers) setConfirmCopy(v)
                    else void copyAnswers(v)
                  }}
                >
                  <SelectTrigger
                    aria-label="Подставить ответы с другой площадки"
                    className="text-left"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_COPY}>Не подставлять — заполнить с нуля</SelectItem>
                    {donors.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        С площадки «{d.name}»
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-2 text-12 text-txt-tertiary">
                Удобно, если площадки похожи: ответы подставятся, отличия можно поправить.
              </p>
            </Card>
          ) : null}

          <p className="text-13 text-txt-secondary">
            Название, ИНН, ОКВЭД и руководитель берутся из реквизитов компании — в опросе их не
            спрашиваем.
          </p>
        </div>
      ) : null}

      {step > 0 && step < REVIEW_STEP ? (
        <Card>
          {visibleEcoQuestions(questions, answers, step).length === 0 ? (
            <p className="text-13 text-txt-secondary">В этом разделе вопросов нет.</p>
          ) : (
            visibleEcoQuestions(questions, answers, step).map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                answer={answers[q.id]}
                onChange={(next) => change(q.id, next)}
              />
            ))
          )}
        </Card>
      ) : null}

      {step === REVIEW_STEP ? (
        <Card
          header={
            <span className="flex flex-wrap items-center justify-between gap-2">
              Проверьте ответы
              <Badge variant={unanswered > 0 ? 'amber' : 'green'}>
                {unanswered > 0 ? `не заполнено: ${unanswered}` : 'все ответы есть'}
              </Badge>
            </span>
          }
        >
          <p className="mb-2 text-13 text-txt-secondary">
            Незаполненное не мешает завершить опрос — менеджер уточнит при разговоре.
          </p>
          {ECO_SURVEY_SECTIONS.map((section, i) => {
            const qs = visibleEcoQuestions(questions, answers, i + 1)
            if (qs.length === 0) return null
            return (
              <section key={section} className="mt-4">
                <h3 className="mb-1 text-12 font-semibold uppercase text-txt-tertiary">
                  {section}
                </h3>
                <ul>
                  {qs.map((q) => {
                    const text = ecoAnswerText(q, answers[q.id])
                    return (
                      <li
                        key={q.id}
                        className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-13 text-txt-secondary">{q.text}</div>
                          <div className={cn('font-medium', text ? 'text-txt' : 'text-amber')}>
                            {text || 'не заполнено'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Изменить ответ «${q.text}»`}
                          onClick={() => void go(q.section)}
                        >
                          Изменить
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </section>
            )
          })}
        </Card>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        {step === 0 ? (
          <Button variant="secondary" onClick={exit.back.onClick}>
            {exit.back.label}
          </Button>
        ) : (
          <Button
            variant="secondary"
            disabled={saving || finishing}
            onClick={() => void go(step - 1)}
            leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
          >
            Назад
          </Button>
        )}
        {step < REVIEW_STEP ? (
          <Button
            disabled={!siteId || loadingSurvey || saving}
            onClick={() => void go(step + 1)}
            rightIcon={<ArrowRight className="size-4 stroke-current" aria-hidden />}
          >
            {nextLabel}
          </Button>
        ) : (
          <Button
            disabled={finishing}
            onClick={() => void finish()}
            leftIcon={<Check className="size-4 stroke-current" aria-hidden />}
          >
            {finishing ? 'Завершение…' : 'Завершить опрос — добавить документы'}
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={confirmCopy !== null}
        title="Подставить ответы"
        message="Ответы этой площадки заменятся ответами с выбранной площадки. Продолжить?"
        onClose={() => setConfirmCopy(null)}
        buttons={[
          {
            label: 'Подставить',
            variant: 'primary',
            onClick: () => {
              const donor = confirmCopy
              setConfirmCopy(null)
              if (donor) void copyAnswers(donor)
            },
          },
          { label: 'Отмена', variant: 'secondary', onClick: () => setConfirmCopy(null) },
        ]}
      />
    </div>
  )
}
